/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import CampaignModel from 'cio/models/campaign';
import { Entities, Periods, REVERSE_PERIOD_KEYS } from 'cio/utils/metrics/common';
import { DateRange } from '@customerio/fly-components/utils/fly-date-picker-utils';
import { Grouping } from 'cio/utils/metrics/resolution';
import { task } from 'ember-concurrency';
import { inject as service } from '@ember/service';
import MetricsService from 'cio/services/metrics';
import { tracked } from '@glimmer/tracking';
import { TrackedResponseMetric, TrackedResponseMetricStat } from 'cio/utils/metrics/models/tracked-response-metric';
import SettingsService from 'cio/services/settings';
import ActionModel from 'cio/models/action';
import isMultiLangBranchChild from 'cio/utils/visual-workflow-builder/is-multi-lang-branch-child';
import { isEmpty } from '@ember/utils';

interface CampaignMetricsPageTrackedResponsesCardArgs {
  campaign: CampaignModel;
  timeRange: DateRange;
  grouping: Grouping;

  onTrackCopyTrackedResponse: () => unknown;
}

export interface TrackedResponseMetricRow {
  action: ActionModel;
  metrics: TrackedResponseMetricStat;
  defaultActionId: string;
  isMultiLanguage: boolean;
  name: string;
  icon: string;
  variations?: TrackedResponseMetricRow[];
}

export default class CampaignMetricsPageTrackedResponsesCard extends Component<CampaignMetricsPageTrackedResponsesCardArgs> {
  @service metrics!: MetricsService;
  @service settings!: SettingsService;

  @tracked responseMetrics: TrackedResponseMetric | null = null;

  fetchMetrics = task({ keepLatest: true }, async () => {
    this.responseMetrics = await this.metrics.fetchTrackedResponseMetric(
      Entities.CAMPAIGN,
      this.args.campaign.id,
      REVERSE_PERIOD_KEYS[this.args.grouping] ?? Periods.DAYS,
      this.args.timeRange,
    );
  });

  get sortedResponseMetricsRows() {
    if (this.fetchMetrics.isRunning) {
      return [];
    }

    this.args.campaign.ensureParsedTree();

    let metricRows = this.args.campaign.workflowActions
      .filter((action) => action.isInApp || action.isMultiLangBranch)
      .reduce<TrackedResponseMetricRow[]>((rows, action) => {
        if (action.isMultiLangBranch) {
          rows = this.appendMultiLanguageBranchRowWithVariations(rows, action);
        } else {
          rows = this.appendDefaultMetricRow(rows, action);
        }

        return rows;
      }, []);

    metricRows.sort((a, b) => {
      return b.metrics.totalResponses - a.metrics.totalResponses;
    });

    return metricRows;
  }

  get hasRows() {
    return !isEmpty(this.sortedResponseMetricsRows);
  }

  private appendMultiLanguageBranchRowWithVariations(
    rows: TrackedResponseMetricRow[],
    action: ActionModel,
  ): TrackedResponseMetricRow[] {
    let childActions = this.args.campaign.getConsolidatedBranchChildActions(action.id);
    let defaultAction = childActions[childActions.length - 1];

    if (childActions.length === 0 || defaultAction == null || !defaultAction.isInApp) {
      return rows;
    }

    let variations = childActions
      .map((a) => {
        let language = this.args.campaign.getConsolidatedBranchChildActionLanguage(a.id);
        if (language === '') {
          language = 'Default';
        }

        return <TrackedResponseMetricRow>{
          action: a,
          metrics: this.responseMetrics?.metrics[a.id],
          defaultActionId: defaultAction.id,
          name: language,
          isMultiLanguage: false,
        };
      })
      .sort((a, b) => {
        if (a.name === 'Default') {
          return -2;
        } else if (b.name === 'Default') {
          return 2;
        }

        return a.name?.localeCompare(b.name ?? '') ?? 0;
      });

    variations = variations.filter((v) => !isEmpty(v.metrics));

    if (variations.length === 0) {
      return rows;
    }

    return [
      ...rows,
      <TrackedResponseMetricRow>{
        action,
        defaultActionId: defaultAction.id,
        name: defaultAction.name,
        icon: defaultAction.info.icon,
        metrics: this.consolidateMetrics(variations),
        isMultiLanguage: true,
        variations,
      },
    ];
  }

  private appendDefaultMetricRow(rows: TrackedResponseMetricRow[], action: ActionModel): TrackedResponseMetricRow[] {
    if (isMultiLangBranchChild(action)) {
      return rows;
    }

    let metrics = this.responseMetrics?.metrics[action.id];

    if (isEmpty(metrics)) {
      return rows;
    }

    return [
      ...rows,
      <TrackedResponseMetricRow>{
        action,
        metrics,
        defaultActionId: action.id,
        name: action.name,
        icon: action.info.icon,
        isMultiLanguage: false,
      },
    ];
  }

  private consolidateMetrics(variations: TrackedResponseMetricRow[]): TrackedResponseMetricStat {
    let consolidatedMetric = new TrackedResponseMetricStat({}, {});

    for (let variation of variations) {
      consolidatedMetric.addMetric(variation.metrics);
    }

    return consolidatedMetric;
  }
}
